import { useFormContext } from 'react-hook-form';
import { contactFieldActions, createContactField } from '@/app/(main)/client-detail/utils/contacts';
const ARRAY_KEYS = [
  'phones',
  'additional_emails',
  'class_preference',
  'accommodation_types',
  'general_interests',
  'beverage_preferences',
  'pillow_preferences',
  'room_configuration_preferences',
  'spa_preferences',
  'spa_preferences_special_requests',
] as const;

export const useGetPayloadData = () => {
  const { getValues } = useFormContext();

  const getPayloadData = (name: string, value: unknown) => {
    const contact = createContactField(name);
    if (contact) {
      const actions = contactFieldActions(
        contact.kind,
        contactValue(contact.kind, name, value, getValues),
        getValues(`${contact.prefix}.client.crm_emails`) ?? [],
        getValues(`${contact.prefix}.client.crm_phones`) ?? []
      );
      if (!actions) return null;

      return { client: actions };
    }

    const parts = name.split('.');

    const isRelatedPassenger = parts[0] === 'related_passengers';
    const basePath = isRelatedPassenger ? parts.slice(2) : parts.slice(1);

    const arrayKey = basePath.find((part) =>
      ARRAY_KEYS.includes(part as (typeof ARRAY_KEYS)[number]),
    );

    if (arrayKey) {
      const arrayIdx = basePath.indexOf(arrayKey);
      const parentPath = basePath.slice(0, arrayIdx).join('.');

      const fullArrayPath = isRelatedPassenger
        ? ['related_passengers', parts[1], ...basePath.slice(0, arrayIdx + 1)].join('.')
        : [parts[0], ...basePath.slice(0, arrayIdx + 1)].join('.');

      const arr = getValues(fullArrayPath);

      return deepSet(parentPath, { [arrayKey]: arr });
    }

    return getFieldNameAsJson(name, value);
  };

  const getPassengerId = (name: string) => {
    if (name.includes('primary_passenger')) {
      return getValues('primary_passenger.client.client_id');
    }

    return getValues(`related_passengers.${name.split('.')[1]}.client.client_id`);
  };

  return {
    getPayloadData,
    getPassengerId,
  };
};

function contactValue(
  kind: 'email' | 'additional_emails' | 'phone' | 'phones',
  name: string,
  value: unknown,
  getValues: (path: string) => unknown
): unknown {
  if (kind === 'email' || kind === 'phone') return value;

  const parts = name.split('.');
  const isRelatedPassenger = parts[0] === 'related_passengers';
  const arrayPath = isRelatedPassenger
    ? [parts[0], parts[1], 'client', kind].join('.')
    : ['primary_passenger', 'client', kind].join('.');

  return getValues(arrayPath);
}

function deepSet(path: string, valueObj: any) {
  if (!path) return valueObj;

  const parts = path.split('.');
  return parts.reverse().reduce((acc, key) => ({ [key]: acc }), valueObj);
}

const getFieldNameAsJson = (fieldString: string, value: unknown) => {
  const parts = fieldString.split('.');
  const rest =
    parts[0] === 'related_passengers' ? parts.slice(2) : parts.slice(1);

  return rest.reduceRight((acc, key) => ({ [key]: acc }), value);
};